<template>
    <div class="d-flex flex-column" style="height: 48em">
        <!-- Header -->
        <header class="flex-shrink-0 p-3 border-bottom">
            <h2 class="editor-header">{{ $t('invoice_history.invoice_details') }}</h2>
        </header>
        <!-- Content -->
        <main class="flex-grow-1 overflow-auto p-3">
            <div class="row mb-2" style="font-size: .9em">
                <div class="col-sm-3"><span class="text-muted">{{ $t('common.id') }}</span>
                    <div>{{ invoice.id }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.status') }}</span>
                    <div><span :class="statusClass(invoice.status)">{{ getStatusText(invoice.status) }}</span></div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.date') }}</span>
                    <div>{{ formatDate(invoice.created) }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.sum') }}</span>
                    <div>{{ formatCurrency(invoice.total_sum / 100) }}</div>
                </div>
            </div>
            <div class="row mb-2" style="font-size: .9em">
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contractor_number') }}</span>
                    <div>{{ invoice.contractor_number || '-' }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.gross_amount') }}</span>
                    <div>{{ formatGrossAmount(invoice.gross_amount) }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.document_date') }}</span>
                    <div>{{ formatDate(invoice.document_date) }}</div>
                </div>
            </div>
            <div v-if="invoice.contract" class="row" style="font-size: .9em">
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contract') }}</span>
                    <div>{{ invoice.contract.id }} - {{ invoice.contract.name }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contract_number') }}</span>
                    <div>{{ invoice.contract.contract_number ? invoice.contract.contract_number : ' - ' }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.customer') }}</span>
                    <div>{{ invoice.contract.customer.name || '-' }}</div>
                </div>
            </div>

            <!-- Credit notes -->
            <div v-if="invoice.credit_notes && invoice.credit_notes.length > 0">
                <hr/>
                <h6 class="mb-3" style="color: #5f8f01"><i class="fas fa-file-invoice me-2"></i>{{ $t('credit_note.credit_notes') }} ({{ invoice.credit_notes.length }})</h6>
                <div v-for="cn in invoice.credit_notes" :key="cn.id" class="card mb-2 p-2"
                     style="border: 1px solid #e0e0e0; border-radius: 8px;">
                    <div class="row mb-1">
                        <div class="col-sm-3"><span class="text-muted small">{{ $t('credit_note.note_id') }}</span>
                            <div>{{ cn.id }}</div>
                        </div>
                        <div class="col-sm-3"><span class="text-muted small">{{ $t('invoice_history.date') }}</span>
                            <div>{{ formatDate(cn.created) }}</div>
                        </div>
                        <div class="col-sm-3"><span class="text-muted small">{{ $t('invoice_history.status') }}</span>
                            <div><span :class="statusClass(cn.status)">{{ getStatusText(cn.status) }}</span></div>
                        </div>
                        <div class="col-sm-3"><span class="text-muted small">{{ $t('invoice_history.sum') }}</span>
                            <div>{{ formatCurrency(cn.total_sum / 100) }}</div>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-sm-3"><span class="text-muted small">{{ $t('invoice_history.contractor_number') }}</span>
                            <div>{{ cn.contractor_number || '-' }}</div>
                        </div>
                        <div class="col-sm-3"><span class="text-muted small">{{ $t('invoice_history.gross_amount') }}</span>
                            <div>{{ formatGrossAmount(cn.gross_amount) }}</div>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div v-if="cn.info" class="col-12">
                            <span class="text-muted small">{{ $t('credit_note.info') }}</span>
                            <div>{{ cn.info }}</div>
                        </div>
                    </div>
                    <table class="table table-sm mb-0 mt-1">
                        <thead>
                        <tr>
                            <th class="small">{{ $t('credit_note.row_id') }}</th>
                            <th class="small">{{ $t('orders.order') }}</th>
                            <th class="small text-end pe-4">{{ $t('invoice_history.sum') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="row in cn.credit_note_rows" :key="row.id" style="font-size: .85em">
                            <td style="width: 100px;">{{ row.invoice_row_id }}</td>
                            <td>{{ getCreditRowOrder(row, invoice) }}</td>
                            <td class="text-end pe-4"  style="width: 200px;">{{ formatCurrency(row.sum / 100) }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Invoice rows -->
            <div v-if="invoice.invoice_rows && invoice.invoice_rows.length > 0">
                <hr/>
                <h6 class="mb-3" style="color: #5f8f01"><i
                    class="fas fa-list me-2"></i>{{ $t('invoice_history.invoice_rows') }} ({{ invoice.invoice_rows?.length }})</h6>
                <div class="mb-3">
                    <vue3-datatable
                        :rows="invoice.invoice_rows"
                        :columns="orderFields"
                        :pagination="false"
                        :sortable="false"
                        :height="invoice.invoice_rows.length < 6 ? '100%' : '30vh'"
                        skin="bh-table-compact"
                        cell-class="routa-table-cell"
                    >
                        <template #order_name="data">
                            {{ getInvoiceRowOrder(data.value.id, invoice) }}
                        </template>
                        <template #time_range="data">
                            {{ getInvoiceRowDates(data.value.id, invoice) }}
                        </template>
                        <template #unit_price="data">
                            {{ formatCurrency(getInvoiceRowUnitPrice(data.value.id, invoice) / 100) }}
                        </template>
                        <template #account="data">
                            {{ getInvoiceRowAccount(data.value.id, invoice) }}
                        </template>
                        <template #invoicing_code="data">
                            {{ getInvoiceRowInvoicingCode(data.value.id, invoice) }}
                        </template>
                        <template #unit="data">
                            {{ pricingModelToText(data.value.items[0].pricing_model) }}
                        </template>
                        <template #amount="data">
                            {{ getInvoiceRowAmount(data.value.id, invoice) }}
                        </template>
                        <template #cost="data">
                            {{ formatCurrency(getInvoiceRowSum(data.value.id, invoice)) }}
                        </template>
                    </vue3-datatable>
                </div>
                <hr/>

                <!-- Invoice items -->
                <h6 class="mb-3" style="color: #5f8f01"><i class="fas fa-money-check me-2"></i>{{ $t('invoice_history.invoice_items') }} ({{ invoiceItems.length }})</h6>
                <div class="mb-3" style="font-size: .8em">
                    <vue3-datatable
                        :rows="invoiceItems"
                        :columns="itemsFields"
                        :pagination="false"
                        :sortable="false"
                        :height="invoiceItems.length < 6 ? '100%' : '30vh'"
                        skin="bh-table-compact"
                        cell-class="routa-table-cell"
                    >
                        <template #order="data">
                            {{ getInvoiceItemOrder(data.value.id, invoice) }}
                        </template>
                        <template #date="data">
                            {{ data.value.date ? getShortDateString(data.value.date) : "-" }}
                        </template>
                        <template #unit="data">
                            {{ data.value.pricing_model ? pricingModelToText(data.value.pricing_model) : '-' }}
                        </template>
                        <template #unit_price="data">
                            {{ data.value.unit_price ? formatCurrency(data.value.unit_price / 100) : '-' }}
                        </template>
                        <template #invoice_amount="data">
                            {{ getInvoiceAmount(data.value) }}
                        </template>
                        <template #invoiceable_sum="data">
                            {{ formatCurrency(data.value.invoiceable_sum) }}
                        </template>
                    </vue3-datatable>
                </div>
            </div>
        </main>

        <!-- Footer -->
        <footer class="flex-shrink-0 p-3 border-top">
            <div class="col-sm-12 my2 d-flex justify-content-end">
                <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                    {{ $t('common.close') }}
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {timeUtils} from '../mixins/TimeUtils'
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {priceHelper} from "../mixins/PricingMixin.js";
import {invoicingHelper} from "../mixins/InvoicingMixin.js";

export default {
    name: 'InvoiceDetails',
    components: {FontAwesomeIcon, Vue3Datatable},
    mixins: [restApi, timeUtils, priceHelper, invoicingHelper],
    props: {
        invoice: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            invoiceItems: []
        }
    },
    computed: {
        orderFields() {
            return [
                {field: 'id', title: this.$t('credit_note.row_id'), sort: true, headerClass: 'routa-table-header'},
                {field: 'time_range', title: this.$t('common.time_range'), sort: true, headerClass: 'routa-table-header'},
                {field: 'order_name', title: this.$t('orders.order'), sort: true, headerClass: 'routa-table-header'},
                {field: 'account', title: this.$t('account.account'), sort: true, headerClass: 'routa-table-header'},
                {field: 'invoicing_code', title: this.$t('account.invoicing_code'), sort: true, headerClass: 'routa-table-header'},
                {field: 'unit_price', title: this.$t('trip_list.unit_price'), sort: true, headerClass: 'routa-table-header'},
                {field: 'unit', title: this.$t('material_reports.unit'), sort: true, headerClass: 'routa-table-header'},
                {field: 'amount', title: this.$t('daily_summary.invoiceable_amount'), sort: true, headerClass: 'routa-table-header'},
                {field: 'cost', title: this.$t('daily_summary.cost', {currency: import.meta.env.VITE_APP_CURRENCY}), sort: true, headerClass: 'routa-table-header'}
            ]
        },
        itemsFields() {
            return [
                {field: 'row_id', title: this.$t('credit_note.row_id'), sort: true, headerClass: 'routa-table-header'},
                {field: 'date', title: this.$t('danger_report.event_time'), sort: true, headerClass: 'routa-table-header'},
                {field: 'order', title: this.$t('orders.order'), sort: true, headerClass: 'routa-table-header'},
                {field: 'vehicle', title: this.$t('trip_list.vehicle'), sort: true, headerClass: 'routa-table-header'},
                {field: 'unit_price', title: this.$t('trip_list.unit_price'), sort: true, headerClass: 'routa-table-header'},
                {field: 'unit', title: this.$t('material_reports.unit'), sort: true, headerClass: 'routa-table-header'},
                {field: 'invoice_amount', title: this.$t('daily_summary.invoiceable_amount'), sort: true, headerClass: 'routa-table-header'},
                {field: 'invoiceable_sum', title: this.$t('daily_summary.cost', {currency: import.meta.env.VITE_APP_CURRENCY}), sort: true, headerClass: 'routa-table-header'}
            ]
        }
    },
    created() {
        const rows = []
        this.invoice.invoice_rows?.forEach(row => {
            row.items.forEach(summary => {
                summary.row_id = row.id
                rows.push(summary)
            })
        })
        this.invoiceItems = rows
    }
}
</script>

<style scoped>
.badge {
    padding: 0.4em 0.6em;
    font-size: 0.85em;
}

/* Remove grey background from table headers */
:deep(.routa-table-header) {
    background-color: transparent !important;
}

/* Add unified border below header row */
:deep(.bh-table-compact thead tr) {
    border-bottom: 1px solid #dee2e6 !important;
}

/* Remove grey background from table body */
:deep(.bh-table-compact) {
    background-color: white !important;
}

:deep(.bh-table-compact tbody) {
    background-color: white !important;
}

:deep(.bh-table-compact tr) {
    background-color: white !important;
}

:deep(.bh-table-responsive .dataTable-bottom .dataTable-info) {
    display: none !important;
}

.type-badge {
    display: inline-block;
    padding: 0.25em 0.6em;
    font-size: 0.85em;
    border: 1px solid #ccc;
    border-radius: 6px;
}

.credit-note-text {
    color: #003956 !important;
    font-style: italic;
}

.cn-badge {
    background-color: #fff3cd;
    border-color: #ffc107;
    color: #856404;
}

</style>
